"use client";
import { Component } from "react";
import ErrorTopLevel from "./ErrorTopLevel";

export const CHUNK_RELOAD_STORAGE_KEY = "nrs-portal:chunk-reload-attempt";
export const CHUNK_RELOAD_MAX_AGE_MS = 60_000;

/**
 * Detect stale deployment/runtime chunk fetch errors raised by webpack.
 */
export function isChunkLoadError(error) {
  if (!error) {
    return false;
  }

  const errorName = String(error.name || "");
  const errorMessage = String(error.message || "");
  const combined = `${errorName} ${errorMessage}`.toLowerCase();

  return combined.includes("chunkloaderror") || combined.includes("loading chunk");
}

/**
 * Reload exactly once for chunk-load mismatches to recover from stale HTML.
 */
export function attemptChunkErrorRecovery(
  error,
  storage = typeof window !== "undefined" ? window.sessionStorage : null,
  locationRef = typeof window !== "undefined" ? window.location : null,
  now = Date.now()
) {
  if (!isChunkLoadError(error) || !storage || !locationRef?.reload) {
    return false;
  }

  const previousAttemptRaw = storage.getItem(CHUNK_RELOAD_STORAGE_KEY);
  const previousAttempt =
    previousAttemptRaw === null ? Number.NaN : Number.parseInt(previousAttemptRaw, 10);
  const hasRecentAttempt =
    Number.isFinite(previousAttempt) && now - previousAttempt < CHUNK_RELOAD_MAX_AGE_MS;

  if (hasRecentAttempt) {
    return false;
  }

  storage.setItem(CHUNK_RELOAD_STORAGE_KEY, String(now));
  locationRef.reload();
  return true;
}

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI.
    console.error("Otherwise uncaught error:", error);
    return { hasError: true, error };
  }

  componentDidCatch(error) {
    attemptChunkErrorRecovery(error);
  }

  //   componentDidCatch(error, errorInfo) {
  //     // You can also log the error to an error reporting service
  //     logErrorToMyService(error, errorInfo);
  //   }

  render() {
    if (this.state.hasError) {
      // Render fallback UI
      return <ErrorTopLevel error={this.state.error} />;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
